import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Calendar, Clock, ChevronDown } from 'lucide-react';
import SEOHead from '../../components/SEOHead';
import BlogNotFound from '../../components/BlogNotFound';
import { getBlogBySlug } from '../../data/blogs';

const Blog07042026 = () => {
  const [activeSection, setActiveSection] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const blog = getBlogBySlug('your-mine-operating-peak-potential-discover');

  useEffect(() => {
    setIsDesktop(window.innerWidth >= 1100);

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);

      const sections = document.querySelectorAll('h2[id]');
      let current = '';

      sections.forEach(section => {
        const sectionTop = section.getBoundingClientRect().top;
        if (sectionTop <= 150) {
          current = section.getAttribute('id') || '';
        }
      });

      setActiveSection(current);
    };

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1100);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -120;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setTocOpen(false);
    }
  };

  const tableOfContents = [
    {
        "id": "context",
        "title": "1. Context",
        "originalTitle": "Context"
    },
    {
        "id": "problem-statement",
        "title": "2. Problem Statement",
        "originalTitle": "Problem Statement"
    },
    {
        "id": "core-framework-ai-in-mining",
        "title": "3. Core Framework: AI in Mining",
        "originalTitle": "Core Framework: AI in Mining"
    },
    {
        "id": "comparative-analysis",
        "title": "4. Comparative Analysis",
        "originalTitle": "Comparative Analysis"
    },
    {
        "id": "business-use-cases",
        "title": "5. Business Use Cases",
        "originalTitle": "Business Use Cases"
    },
    {
        "id": "benefits-outcomes",
        "title": "6. Benefits & Outcomes",
        "originalTitle": "Benefits & Outcomes"
    },
    {
        "id": "challenges-realities",
        "title": "7. Challenges & Realities",
        "originalTitle": "Challenges & Realities"
    },
    {
        "id": "future-outlook",
        "title": "8. Future Outlook",
        "originalTitle": "Future Outlook"
    },
    {
        "id": "conclusion",
        "title": "9. Conclusion",
        "originalTitle": "Conclusion"
    },
    {
        "id": "call-to-action",
        "title": "10. Call to Action",
        "originalTitle": "Call to Action"
    }
];

  const faqData = [
    {
        "question": "What is this technology and how does it work?",
        "answer": "This technology represents a significant advancement in the field, offering innovative solutions to common challenges through modern approaches and proven methodologies."
    },
    {
        "question": "Who can benefit from implementing this solution?",
        "answer": "Organizations of all sizes can benefit, particularly those looking to improve efficiency, reduce costs, and enhance their competitive advantage through technological innovation."
    },
    {
        "question": "What are the main challenges in implementation?",
        "answer": "Key challenges include initial setup complexity, integration with existing systems, and ensuring proper training. However, with proper planning and support, these can be effectively managed."
    },
    {
        "question": "What ROI can be expected?",
        "answer": "While results vary by organization, typical implementations show significant improvements in operational efficiency, cost reduction, and enhanced capabilities within the first year."
    }
];

  const tldrPoints = [
    "Africa's mining sector, a cornerstone of many economies, is currently undergoing a profound digital transformation. At the heart of this shift lies th...",
    "Traditional mining operations in Africa often grapple with significant challenges:",
    "AI in mining refers to the application of intelligent algorithms and systems to optimize various aspects of the mining value chain, from exploration t..."
];

  const keyTakeaways = [
    "Fast implementation strategies deliver measurable ROI within weeks, not months",
    "Agile methodologies reduce time-to-production by 60-80% compared to traditional approaches",
    "Cloud-native architecture enables rapid scaling without infrastructure bottlenecks",
    "Automated workflows eliminate manual bottlenecks and accelerate delivery timelines",
    "Real-time analytics provide immediate insights for faster decision-making"
];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": blog?.title,
        "description": blog?.excerpt,
        "image": blog?.featuredImage,
        "datePublished": blog?.publishDate,
        "dateModified": blog?.publishDate,
        "author": {
          "@type": "Organization",
          "name": "Agility"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Agility",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.agilitytech.ai/agility hex fonts.png"
          }
        },
        "keywords": blog?.seoKeywords.join(', '),
        "articleBody": "Is Your Mine Operating at Peak Potential? Discover How AI is Reshaping Africa’s Mining Sector!",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://www.agilitytech.ai/insights/${blog?.slug}`
        },
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": [".tldr-section", ".key-takeaways-section"]
        }
      },
      {
        "@type": "HowTo",
        "name": blog?.title,
        "description": blog?.excerpt,
        "step": keyTakeaways.map((takeaway, index) => ({
          "@type": "HowToStep",
          "position": index + 1,
          "name": `Step ${index + 1}`,
          "text": takeaway
        }))
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  if (!blog) return <BlogNotFound />;

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      <SEOHead
        title={`${blog?.title} | Agility Blog`}
        description={blog?.excerpt}
        keywords={blog?.seoKeywords.join(', ')}
        url={`https://www.agilitytech.ai/insights/${blog?.slug}`}
        image={blog?.featuredImage}
        type="article"
        article={true}
        author={blog?.author}
        publishedTime={new Date(blog?.publishDate || '').toISOString()}
        modifiedTime={new Date(blog?.publishDate || '').toISOString()}
        structuredData={structuredData}
      />

      {/* Blog Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'linear-gradient(to bottom, #0B0B0B, #050505)' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '1200px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div className="mb-6">
              <span style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: 'rgba(20, 184, 166, 0.15)',
                color: '#14b8a6',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: '500',
                border: '1px solid rgba(20, 184, 166, 0.3)'
              }}>
                {blog.category}
              </span>
            </div>

            <h1 style={{
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontFamily: 'Sora, sans-serif',
              fontWeight: '700',
              color: '#ffffff',
              marginBottom: '1.5rem',
              lineHeight: '1.2',
              letterSpacing: '-0.02em'
            }}>
              {blog.title}
            </h1>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', color: '#9ca3af', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={16} />
                <span style={{ fontSize: '0.875rem' }}>{new Date(blog.publishDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={16} />
                <span style={{ fontSize: '0.875rem' }}>{blog.publishDate}</span>
              </div>
            </div>

            <p style={{
              fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
              color: '#d1d5db',
              lineHeight: '1.75',
              marginBottom: '2rem'
            }}>
              {blog.excerpt}
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {blog.tags.map((tag, index) => (
                <span key={index} style={{
                  padding: '0.25rem 0.75rem',
                  background: '#121212',
                  color: '#14b8a6',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  border: '1px solid #262626'
                }}>
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section style={{ background: '#050505', paddingBottom: '5rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem', display: 'flex', minHeight: '100vh', gap: '3rem', flexDirection: isDesktop ? 'row' : 'column' }}>
          {/* Sidebar TOC - Desktop */}
          {isDesktop && (
            <div style={{
              position: 'sticky',
              top: '100px',
              height: 'fit-content',
              width: '260px',
              flexShrink: 0,
              alignSelf: 'flex-start',
              zIndex: 999
            }}>
              <h3 style={{
                color: '#ffffff',
                fontWeight: '600',
                marginBottom: '1rem',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>Table of Contents</h3>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {tableOfContents.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      padding: '0.5rem 1rem',
                      borderRadius: '0.5rem',
                      transition: 'all 0.2s',
                      borderLeft: activeSection === item.id ? '2px solid #14b8a6' : '2px solid transparent',
                      background: activeSection === item.id ? 'rgba(20,184,166,0.15)' : 'transparent',
                      color: activeSection === item.id ? '#14b8a6' : '#9ca3af',
                      cursor: 'pointer',
                      border: 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.color = '#d1d5db';
                        e.currentTarget.style.background = '#121212';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.color = '#9ca3af';
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    {item.title}
                  </button>
                ))}
              </nav>
            </div>
          )}

          {/* Mobile TOC */}
          {!isDesktop && (
            <div style={{
              width: '100%',
              marginBottom: '2rem'
            }}>
              <div style={{
                background: '#121212',
                borderRadius: '12px',
                border: '1px solid #262626',
                padding: '1rem'
              }}>
                <button
                  onClick={() => setTocOpen(!tocOpen)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <span>Table of Contents</span>
                  <ChevronDown style={{
                    transform: tocOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }} />
                </button>
                {tocOpen && (
                  <nav style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {tableOfContents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.5rem 1rem',
                          background: 'none',
                          border: 'none',
                          color: activeSection === item.id ? '#14b8a6' : '#9ca3af',
                          fontSize: '0.875rem',
                          cursor: 'pointer'
                        }}
                      >
                        {item.title}
                      </button>
                    ))}
                  </nav>
                )}
              </div>
            </div>
          )}

          {/* Article Content */}
          <div style={{ flex: 1 }}>
            <article style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>

              {/* TL;DR Section - AEO Optimized */}
              <div className="tldr-section" style={{
                background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.1), rgba(20, 184, 166, 0.05))',
                borderLeft: '4px solid #14b8a6',
                borderRadius: '12px',
                padding: '2rem',
                marginBottom: '3rem',
                border: '1px solid rgba(20, 184, 166, 0.2)'
              }}>
                <h2 style={{
                  color: '#14b8a6',
                  fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: '700',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    background: '#14b8a6',
                    color: '#000000',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '700'
                  }}>TL;DR</span>
                  Quick Summary
                </h2>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}>
                  {tldrPoints.map((point, idx) => (
                    <li key={idx} style={{
                      color: '#e5e7eb',
                      fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                      lineHeight: '1.7',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem'
                    }}>
                      <span style={{
                        color: '#14b8a6',
                        fontWeight: '700',
                        fontSize: '1.25rem',
                        lineHeight: '1'
                      }}>•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              
              <h2 id="context" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Context
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Africa's mining sector, a cornerstone of many economies, is currently undergoing a profound digital transformation. At the heart of this shift lies the rapid adoption of Artificial Intelligence (AI), moving beyond traditional methods to embrace data-driven insights. This trend is not merely a technological upgrade but a strategic imperative, driven by the need to unlock new efficiencies, enhance safety, and discover previously untapped mineral wealth across the continent. With an estimated 30% of the world's mineral reserves located in Africa, the continent stands on the cusp of a data-driven mining boom.
              </p>

              <h2 id="problem-statement" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Problem Statement
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Traditional mining operations in Africa often grapple with significant challenges:
              </p>

              <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
                
              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Operational Inefficiencies: Reactive maintenance leads to costly equipment downtime and unpredictable production schedules.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                High Exploration Costs & Low Success Rates: Conventional geological mapping can be slow, resource-intensive, and sometimes miss subtle indicators of mineral deposits.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Safety Hazards: Manual operations and human error contribute to a higher risk of accidents, despite significant improvements over the years.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Resource Management: Inefficient use of resources and energy impacts profitability and environmental sustainability.
              </li>
              </ul>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                These issues directly impact the bottom line, driving up operational costs and hindering the sector's ability to maximize its vast potential.
              </p>

              <h2 id="core-framework-ai-in-mining" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Core Framework: AI in Mining
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                AI in mining refers to the application of intelligent algorithms and systems to optimize various aspects of the mining value chain, from exploration to extraction and processing.
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Artificial Intelligence in the mining industry leverages machine learning, computer vision, and advanced data analytics to interpret complex datasets, automate tasks, and make predictive decisions. This encompasses everything from identifying geological patterns to optimizing machinery performance and improving worker safety.
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                1.  Predictive Maintenance: Sensors installed on mining equipment collect real-time data (vibration, temperature, pressure). AI algorithms analyze this data to detect anomalies and predict potential failures *before* they occur, enabling proactive repairs and minimizing downtime. McKinsey findings suggest AI-driven predictive maintenance can reduce equipment downtime by up to 50% and lower maintenance costs by 10% to 40%.
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                2.  Advanced Geological Mapping & Exploration: Machine learning algorithms process vast geological, geochemical, and geophysical datasets, including satellite imagery and drone surveys. By identifying complex patterns, AI can pinpoint promising areas for mineral deposits far faster and more accurately than traditional methods, enhancing discovery rates and accelerating exploration timelines significantly.
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                3.  Operational Optimization: AI is used to optimize mine planning, production schedules, and logistics. It can dynamically adjust processes based on real-time data, improve resource allocation, and manage supply chains more efficiently, leading to reduced costs and increased productivity.
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                4.  Enhanced Safety: AI-powered monitoring tools, such as computer vision systems, analyze video footage from underground cameras to identify unsafe behaviors or potential hazards, augmenting human capabilities and improving safety protocols.
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Despite its transformative potential, AI adoption in African mining faces hurdles. These include uneven deployment, particularly concentrated in well-capitalized operations. Connectivity infrastructure remains a primary barrier in many emerging mining economies. Furthermore, the high capital intensity and operational complexity of mining mean AI investments compete with other essential capital expenditures, and isolating AI's precise contribution to revenue uplift can be challenging.
              </p>

              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Core Framework: AI in Mining
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03))'
                }}>
                  <img
                    src="https://images.pexels.com/photos/1181263/pexels-photo-1181263.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080"
                    alt="Core Framework: AI in Mining"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop&q=90&auto=format';
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block'
                    }}
                  />
                </div>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Visual representation of core framework: ai in mining concepts and implementation strategies.
                </p>
              </div>

              <h2 id="comparative-analysis" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Comparative Analysis
              </h2>

              <div style={{
                overflowX: 'auto',
                margin: '2rem 0',
                border: '1px solid #262626',
                borderRadius: '8px'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'clamp(0.875rem, 1.8vw, 1rem)',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  <thead>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <th style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#14b8a6',
                        borderBottom: '2px solid #14b8a6'
                      }}>
                        Feature
                      </th>
                      <th style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#14b8a6',
                        borderBottom: '2px solid #14b8a6'
                      }}>
                        Traditional Mining Approaches
                      </th>
                      <th style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#14b8a6',
                        borderBottom: '2px solid #14b8a6'
                      }}>
                        AI-Driven Mining Approaches
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Exploration
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Manual data analysis, extensive drilling, longer timelines.
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        ML algorithms analyze vast datasets; faster, more accurate target identification, reduced exploration timelines.
                      </td>
                    </tr>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Maintenance
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Reactive repairs, scheduled maintenance, significant downtime.
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Predictive maintenance; early fault detection, reduced downtime (up to 50%), lower maintenance costs (10-40%).
                      </td>
                    </tr>
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Operational Efficiency
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Manual adjustments, siloed data, slower decision-making.
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Real-time monitoring, data-driven optimization, dynamic planning, improved resource allocation.
                      </td>
                    </tr>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Safety
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Human observation, manual checks, retrospective analysis.
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        AI-powered monitoring, real-time hazard detection, proactive safety alerts.
                      </td>
                    </tr>
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Cost Implications
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        High operational costs due to inefficiencies and downtime.
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Significant reductions in operating expenses, energy consumption, and capital expenditure through optimization.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 id="business-use-cases" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Business Use Cases
              </h2>

              <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
                
              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: Inefficient and risky mineral exploration processes.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: Accelerated Discovery & Reduced Costs. AI-driven prospecting models analyze geological, geochemical, and geophysical data to pinpoint promising resource deposits with greater accuracy, potentially reducing exploration costs by up to 20% and shortening discovery timelines.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: Frequent equipment breakdowns leading to costly downtime.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: Maximized Uptime & Lower Maintenance Expenses. Predictive maintenance systems, powered by AI, analyze sensor data from haul trucks, drills, and excavators to anticipate failures, allowing for proactive servicing and reducing unscheduled downtime by as much as 50% and maintenance costs by 10-40%.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: High safety risks and human error in hazardous environments.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: Enhanced Worker Safety & Operational Compliance. AI-driven computer vision and sensor networks monitor work sites for unsafe practices or geological instabilities, leading to earlier detection of hazards. South Africa's mining industry, through various advancements including digital technologies, has seen a 91% reduction in fatalities since 1994.
              </li>
              </ul>

              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Business Use Cases
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03))'
                }}>
                  <img
                    src="https://images.pexels.com/photos/590020/pexels-photo-590020.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080"
                    alt="Business Use Cases"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop&q=90&auto=format';
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block'
                    }}
                  />
                </div>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Visual representation of business use cases concepts and implementation strategies.
                </p>
              </div>

              <h2 id="benefits-outcomes" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Benefits & Outcomes
              </h2>

              <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
                
              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Superior Data Processing: AI algorithms can analyze vast geological and operational datasets orders of magnitude faster than human analysts, revealing hidden patterns and insights.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Enhanced Accuracy: Machine learning models improve the precision of geological mapping and predictive analytics, leading to more accurate mineral identification and failure predictions.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Real-time Monitoring & Control: AI systems enable continuous, real-time monitoring of equipment performance, production metrics, and safety parameters, facilitating immediate data-driven decision-making.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Automation of Repetitive Tasks: AI-powered automation reduces manual labor in hazardous or repetitive tasks, improving consistency and reducing human error.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Significant Cost Reduction: Early adopters have reported notable reductions in operational costs, including a potential 10% to 40% reduction in maintenance expenses through predictive maintenance.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Increased Efficiency & Productivity: Optimizes equipment performance, production schedules, and overall operational workflows, leading to higher output and resource utilization.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Improved Safety Records: AI contributes to a safer working environment by identifying potential hazards and unsafe behaviors, leading to a reduction in incidents and fatalities.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Extended Equipment Lifespan: Predictive maintenance minimizes wear and tear and prevents catastrophic failures, extending the operational life of critical mining infrastructure.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Faster Time to Market: Accelerated exploration and development timelines mean quicker access to new mineral deposits, enhancing competitiveness.
              </li>
              </ul>

              <h2 id="challenges-realities" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Challenges & Realities
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                While the promise of AI in African mining is immense, its full realization faces practical challenges. Uneven adoption persists, with more mature, well-capitalized operations leading the way, while smaller players lag due to capital constraints and infrastructure gaps, particularly regarding robust connectivity. There's also a need for skilled talent to implement and manage these advanced systems. Crucially, successful adoption requires strong leadership and a clear strategy to integrate AI into existing workflows, ensuring proper governance and accountability rather than just a technology rollout.
              </p>

              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Challenges & Realities
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03))'
                }}>
                  <img
                    src="https://images.pexels.com/photos/270348/pexels-photo-270348.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080"
                    alt="Challenges & Realities"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop&q=90&auto=format';
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block'
                    }}
                  />
                </div>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Visual representation of challenges & realities concepts and implementation strategies.
                </p>
              </div>

              <h2 id="future-outlook" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Future Outlook
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Over the next 12 months and beyond, AI's role in Africa's mining sector is expected to accelerate, driven by continued investment and innovation. We anticipate a greater push towards integrated AI platforms that unify data across the entire mining value chain. The trend of increased automation will intensify, with autonomous haul trucks, self-driving drillers, and drones becoming more commonplace. By 2035, an estimated 35% of mining jobs could be automated, with 30% to 40% of mining machinery potentially autonomous by 2040. This convergence of AI and workforce transformation will be critical for safer, more efficient, and sustainable mining operations.
              </p>

              <h2 id="conclusion" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Conclusion
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Artificial intelligence stands as a pivotal force in the digital transformation of Africa's mining sector. By providing unparalleled data-driven insights, enabling predictive capabilities, and enhancing automation, AI offers a clear path to significantly reduce operational costs, boost efficiency, improve safety, and unlock new mineral discoveries. While challenges in infrastructure and adoption exist, the strategic value AI brings is undeniable, positioning Africa for a data-powered mining future.
              </p>

              <h2 id="call-to-action" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Call to Action
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Ready to unearth the full potential of AI for your mining operations? Contact us today for a complimentary consultation or to discuss a Proof of Concept tailored to your specific needs. Let's build a smarter, safer, and more profitable future for African mining together.
              </p>


              {/* Key Takeaways Section - AEO Optimized */}
              <div className="key-takeaways-section" style={{
                background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.1), rgba(20, 184, 166, 0.05))',
                borderLeft: '4px solid #14b8a6',
                borderRadius: '12px',
                padding: '2rem',
                marginTop: '3rem',
                marginBottom: '3rem',
                border: '1px solid rgba(20, 184, 166, 0.2)'
              }}>
                <h2 style={{
                  color: '#14b8a6',
                  fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: '700',
                  marginBottom: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    background: '#14b8a6',
                    color: '#000000',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '700'
                  }}>⚡</span>
                  Key Takeaways - Fast Implementation Insights
                </h2>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {keyTakeaways.map((takeaway, idx) => (
                    <li key={idx} style={{
                      color: '#ffffff',
                      fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                      lineHeight: '1.7',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '1rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(20, 184, 166, 0.1)'
                    }}>
                      <span style={{
                        color: '#14b8a6',
                        fontWeight: '700',
                        fontSize: '1.125rem',
                        minWidth: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(20, 184, 166, 0.2)',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        flexShrink: 0
                      }}>{idx + 1}</span>
                      <span>{takeaway}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* FAQ Section */}
              <h2 id="faq" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Frequently Asked Questions
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '3rem' }}>
                {faqData.map((faq, idx) => (
                  <div key={idx} style={{
                    background: '#121212',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    border: '1px solid #262626',
                    transition: 'border-color 0.3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#14b8a6'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#262626'}>
                    <h3 style={{
                      color: '#ffffff',
                      fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
                      fontWeight: '600',
                      marginBottom: '0.75rem',
                      fontFamily: 'Sora, sans-serif',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem'
                    }}>
                      <span style={{ color: '#14b8a6', flexShrink: 0 }}>Q{idx + 1}.</span>
                      <span>{faq.question}</span>
                    </h3>
                    <p style={{
                      color: '#e5e7eb',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                      lineHeight: '1.75',
                      marginLeft: '2rem',
                      fontFamily: 'Inter, sans-serif'
                    }}>
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>

              {/* CTA Section */}
              <div style={{
                marginTop: '3rem',
                background: '#121212',
                borderRadius: '16px',
                padding: 'clamp(1.5rem, 4vw, 2rem)',
                border: '1px solid #262626',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '384px',
                  height: '384px',
                  background: 'rgba(20, 184, 166, 0.1)',
                  borderRadius: '50%',
                  filter: 'blur(80px)'
                }}></div>
                <div style={{ position: 'relative', zIndex: 10, textAlign: 'center' }}>
                  <h3 style={{
                    fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                    fontWeight: '700',
                    color: '#ffffff',
                    marginBottom: '1rem',
                    fontFamily: 'Sora, sans-serif'
                  }}>
                    Ready to Transform Your Business?
                  </h3>
                  <p style={{
                    color: '#e5e7eb',
                    fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                    marginBottom: '1.5rem',
                    maxWidth: '600px',
                    margin: '0 auto 1.5rem',
                    fontFamily: 'Inter, sans-serif'
                  }}>
                    Contact us today for a personalized consultation and discover how we can help you achieve your goals.
                  </p>
                  <Link
                    to="/contact"
                    style={{
                      display: 'inline-block',
                      padding: 'clamp(0.625rem, 2vw, 0.75rem) clamp(1.25rem, 3vw, 1.5rem)',
                      border: '2px solid #14b8a6',
                      color: '#14b8a6',
                      borderRadius: '8px',
                      fontWeight: '600',
                      textDecoration: 'none',
                      transition: 'all 0.3s',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#14b8a6';
                      e.currentTarget.style.color = '#ffffff';
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(20, 184, 166, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#14b8a6';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    Get Started Today
                  </Link>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: 'clamp(1.5rem, 4vw, 2rem)',
            right: 'clamp(1.5rem, 4vw, 2rem)',
            width: 'clamp(44px, 10vw, 48px)',
            height: 'clamp(44px, 10vw, 48px)',
            background: '#121212',
            border: '2px solid #262626',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#14b8a6',
            cursor: 'pointer',
            transition: 'all 0.3s',
            zIndex: 50
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#14b8a6';
            e.currentTarget.style.boxShadow = '0 0 20px rgba(20, 184, 166, 0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#262626';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <ArrowUp size={20} />
        </button>
      )}
    </div>
  );
};

export default Blog07042026;
